/*
 * TELV1-307 — the dashboard's axios instances (axiosInstance,
 * axiosInstanceNoAuth, axiosInstanceFormData, AxiosInstanceAnalyze) reject
 * with `error.response` directly via `Promise.reject(error.response)` —
 * NOT the full AxiosError. So caught errors arrive with shape
 * `{ status, data, ... }`, not `{ response: { status, data } }`. The chat
 * app's axiosInstanceChat does the same.
 *
 * Consumers MUST read errors via these helpers, never `err.response.X`
 * directly, otherwise:
 *  - `getErrorStatus(err)` works for both shapes ✓
 *  - inline `err.response.status` is undefined for the unwrapped shape ✗
 *    (this caused the org-invite "expired" misroute documented in TELV1-307
 *    — the PROFILE_INCOMPLETE check failed because `err.response.data.code`
 *    was undefined, so the fallback 403 branch fired and redirected to the
 *    expired page instead of routing to forgot-password)
 *
 * Long-term cleanup option tracked separately: normalise to standard axios
 * shape by removing the `Promise.reject(error.response)` unwrap. Until then
 * these helpers are the canonical access pattern.
 *
 * Note on typing: every parameter is `unknown` rather than the more
 * specific `AxiosError` because (a) the unwrapped-response shape isn't
 * an AxiosError instance (it's the response object the interceptor
 * passed to `Promise.reject`), and (b) callers can pass arbitrary
 * thrown values without first narrowing. The helpers defensively
 * structural-type-check what they need.
 */

/**
 * Robust read for the human-readable error message. Walks both shapes
 * (wrapped `error.response.data.message` and unwrapped `error.data.message`)
 * plus the JS `Error.message` fallback. Falls back to a generic copy when
 * nothing useful is available.
 */
export const getErrorMessage = (error: unknown, fallbackMessage: string | null = null) => {
  const err = error as {
    response?: { data?: { message?: string } };
    data?: { message?: string };
    message?: string;
  } | null;
  return (
    err?.response?.data?.message ||
    err?.data?.message ||
    err?.message ||
    fallbackMessage ||
    'An unexpected error occurred. Please try again.'
  );
};

/**
 * Robust read for the HTTP status. Returns `'unknown'` when no status is
 * present. Callers should compare numeric (e.g. `=== 403`) — the
 * `'unknown'` string sentinel never equals a `StatusCodes.*` value.
 */
export const getErrorStatus = (error: unknown) => {
  const err = error as {
    response?: { status?: number };
    status?: number;
  } | null;
  return err?.response?.status || err?.status || 'unknown';
};

/**
 * Robust read for the response body object (not just `.message`). Returns
 * the body as `Record<string, unknown>` for arbitrary field access (e.g.
 * `code`, `redirect_to`, `email`) — empty object if no body present so
 * callers can chain `?.` safely.
 *
 * Use when you need a non-message field from the response, e.g.:
 *   const body = getErrorBody(err);
 *   if (body.code === 'PROFILE_INCOMPLETE') { ... }
 */
export const getErrorBody = (error: unknown): Record<string, unknown> => {
  const wrapped = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (wrapped && typeof wrapped === 'object') {
    return wrapped as Record<string, unknown>;
  }
  const unwrapped = (error as { data?: unknown } | null)?.data;
  if (unwrapped && typeof unwrapped === 'object') {
    return unwrapped as Record<string, unknown>;
  }
  return {};
};
